import { useState } from "react";
import { WHITE_KEYS, BLACK_KEYS, noteName } from "@/data/modes";
import { playNote } from "@/lib/audio";
import { useT, noteLabel } from "@/i18n";

export const Piano = ({
  activeMode,
  playingMidi,
  onKeyPress,
  hideLabels = false,
  correctMidi = null,
  wrongMidi = null,
}) => {
  const [pressed, setPressed] = useState(null);
  const { lang } = useT();

  const activeSet = new Set(activeMode?.scale || []);
  const rootMidi = activeMode?.root;
  const flashSet = new Set(
    Array.isArray(playingMidi) ? playingMidi : playingMidi != null ? [playingMidi] : []
  );

  const hit = (midi) => {
    playNote(midi);
    setPressed(midi);
    setTimeout(() => setPressed((p) => (p === midi ? null : p)), 220);
    if (onKeyPress) onKeyPress(midi);
  };

  const whiteWidth = 100 / WHITE_KEYS.length;

  return (
    <div
      className="piano-wrap"
      data-testid="piano-keyboard"
      role="group"
      aria-label="Piano keyboard"
    >
      {!hideLabels && lang === "es" && (
        <p className="solfege-legend" data-testid="solfege-legend">
          Los nombres en paréntesis son las notas en solfeo.
        </p>
      )}
      <div className="relative w-full" style={{ height: "clamp(160px, min(30vw, 30vh), 260px)" }}>
        {/* White keys */}
        <div className="absolute inset-0 flex">
          {WHITE_KEYS.map((k) => {
            const inScale = activeSet.has(k.midi);
            const isRoot = k.midi === rootMidi;
            const isDown = pressed === k.midi || flashSet.has(k.midi);
            const isCorrect = correctMidi === k.midi;
            const isWrong = wrongMidi === k.midi;
            return (
              <button
                key={`w-${k.midi}`}
                data-testid={`piano-key-${noteName(k.midi)}`}
                onMouseDown={() => hit(k.midi)}
                onTouchStart={(e) => {
                  e.preventDefault();
                  hit(k.midi);
                }}
                className={`piano-key-white ${inScale ? "in-scale" : ""} ${
                  isRoot ? "is-root" : ""
                } ${isDown ? "is-down" : ""} ${isCorrect ? "is-correct" : ""} ${
                  isWrong ? "is-wrong" : ""
                }`}
                style={{ width: `${whiteWidth}%` }}
                aria-label={noteName(k.midi)}
              >
                {!hideLabels && (
                  <span className="key-label">
                    {isRoot ? (
                      <span className={`root-badge${lang === "es" ? " label-long" : ""}`}>{noteLabel(k.name, lang)}</span>
                    ) : (
                      <span className={`key-name${lang === "es" ? " label-long" : ""}`}>{noteLabel(k.name, lang)}</span>
                    )}
                  </span>
                )}
              </button>
            );
          })}
        </div>

        {/* Black keys */}
        <div className="absolute inset-0 pointer-events-none">
          {BLACK_KEYS.map((b) => {
            const idx = WHITE_KEYS.findIndex((w) => w.midi === b.after);
            const isDown = pressed === b.midi || flashSet.has(b.midi);
            const inScale = activeSet.has(b.midi);
            const isRoot = b.midi === rootMidi;
            const isCorrect = correctMidi === b.midi;
            const isWrong = wrongMidi === b.midi;
            const left = (idx + 1) * whiteWidth;
            return (
              <button
                key={`b-${b.midi}`}
                data-testid={`piano-key-${noteName(b.midi)}`}
                onMouseDown={() => hit(b.midi)}
                onTouchStart={(e) => {
                  e.preventDefault();
                  hit(b.midi);
                }}
                className={`piano-key-black ${inScale ? "in-scale" : ""} ${
                  isRoot ? "is-root" : ""
                } ${isDown ? "is-down" : ""} ${isCorrect ? "is-correct" : ""} ${
                  isWrong ? "is-wrong" : ""
                }`}
                style={{
                  left: `${left}%`,
                  width: `${whiteWidth * 0.62}%`,
                }}
                aria-label={noteName(b.midi)}
              />
            );
          })}
        </div>
      </div>
    </div>
  );
};
